import { FC, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { config } from "config/config";
import { hashes } from "constants/license-flags";
import Role from "constants/role";
import { identify } from "helpers/analytics";
import { isInIframe } from "helpers/app";
import { installBeamer } from "helpers/beamer";
import { eventSignup, initializeGTM, installGTM, shouldSendSignupEvent } from "helpers/gtm";
import { useOrganization } from "hooks/use-organizations";
import { selectHasBusinessEmail, selectLoggedInAgent } from "store/entities/agents";
import { selectHasLiveChatIntegration } from "store/entities/integrations";
import { LicenseActions, selectLicense, selectSignupGoogleAnalyticsEventSent } from "store/entities/license";
import { selectCurrentSubscription } from "store/entities/subscriptions";

export const Analytics: FC = () => {
  const dispatch = useDispatch();
  const { organization, isLoading } = useOrganization();
  const analyticsSetUp = useRef(false);

  const shouldInstallBeamer = !isInIframe();
  const shouldInstallGTM = config.env !== "dev";

  const loggedInAgent = useSelector(selectLoggedInAgent);
  const loggedInAgentId = loggedInAgent?.id;

  const license = useSelector(selectLicense);
  const subscription = useSelector(selectCurrentSubscription);
  const hasLivechat = useSelector(selectHasLiveChatIntegration);
  const hasKnowledgebase = organization?.products?.some(product => product.product === "KnowledgeBase");

  const isBusinessEmail = useSelector(selectHasBusinessEmail);

  const hasSentSignupGoogleAnalyticsEvent = useSelector(selectSignupGoogleAnalyticsEventSent);

  useEffect(() => {
    if (!shouldInstallBeamer || isLoading || analyticsSetUp.current) return;

    const identity = identify(loggedInAgent, license, subscription, hasLivechat, hasKnowledgebase, isBusinessEmail);

    analyticsSetUp.current = true;

    if (shouldInstallGTM) {
      installGTM(config.googleTagManagerId);
      if (loggedInAgentId) {
        initializeGTM(loggedInAgent);
      }
    }

    if (
      shouldSendSignupEvent(
        license.createdAt,
        loggedInAgent.permission === Role.Owner,
        hasSentSignupGoogleAnalyticsEvent
      ) &&
      shouldInstallGTM
    ) {
      eventSignup(identity, isBusinessEmail ? "business" : "private", license.createdAt);
      dispatch(
        LicenseActions.setLicenseFlag({
          flag: "signupGoogleAnalyticsEventSent",
          hash: hashes.signupGoogleAnalyticsEventSent,
          value: true,
        })
      );
    }

    if (shouldInstallBeamer) installBeamer(identity);
  }, [loggedInAgent, license, subscription, hasLivechat, organization, isLoading, hasKnowledgebase]);

  return null;
};
